import { isMail } from 'components/mail/scheduleSlots';
import StyledInput from 'components/styledInput/StyledInput';
import { useEffect, useRef, useState } from 'react';
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';

// A person's position (user.position on the api).
const POSITION_MAX_LENGTH = 100;

const KINDS = {
  email: { isValid: isMail, shown: (value) => value, input: 'email', caseless: true },
  // A number is kept in E.164 and shown with its country code, grouped.
  phone: { isValid: (value) => isValidPhoneNumber(value), shown: (value) => formatPhoneNumberIntl(value) || value, input: 'phone', caseless: true },
  // Free text, of which the case is kept.
  position: { isValid: (value) => value.length <= POSITION_MAX_LENGTH, shown: (value) => value, input: 'text', caseless: false },
};

// How a record shows an e-mail or a number of this kind.
export const contactLabel = (kind, value) => (value ? KINDS[kind].shown(value) : '');

/**
 * An e-mail or a number of a record (`kind`), read and edited in place: a
 * click opens the field (StyledInput), Entrée or leaving it saves, Échap gives
 * up, an empty field unsets it, unless `required` (then `requiredText`).
 * `onSave(value)` returns a promise; while it fails, the field stays open.
 */
const InlineContactField = ({ kind, value, placeholder, invalidText, required = false, requiredText, onSave }) => {
  const { isValid, shown, input, caseless } = KINDS[kind];
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const editing = draft !== null;
  // The field opens ready to type: its number input, not its country list.
  useEffect(() => {
    if (editing) ref.current?.querySelector('input')?.focus();
  }, [editing]);
  const typed = (draft ?? '').trim();
  const invalid = Boolean(typed) && !isValid(typed);
  const missing = required && !typed;
  const same = caseless ? typed.toLowerCase() === (value ?? '').toLowerCase() : typed === (value ?? '');

  const save = () => {
    if (draft === null || busy || invalid || missing) return;
    if (same) {
      setDraft(null);
      return;
    }
    setBusy(true);
    onSave(typed).then(
      () => {
        setBusy(false);
        setDraft(null);
      },
      () => setBusy(false),
    );
  };

  if (!editing)
    return (
      <button
        type="button"
        onClick={() => setDraft(value ?? '')}
        className={`-ml-1 max-w-full truncate rounded px-1 text-left transition-colors hover:bg-gray-100 ${
          value ? 'text-gray-900' : 'text-gray-400'
        }`}
      >
        {value ? shown(value) : placeholder}
      </button>
    );

  return (
    <div
      ref={ref}
      // Leaving the field saves; moving to its own country list does not.
      onBlur={(event) => !event.currentTarget.contains(event.relatedTarget) && save()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') save();
        if (event.key === 'Escape') setDraft(null);
      }}
    >
      <StyledInput
        type={input}
        value={draft}
        onChange={(next) => setDraft(next?.target ? next.target.value : next)}
        error={invalid ? invalidText : missing ? requiredText : undefined}
        isDisabled={busy}
      />
    </div>
  );
};

export default InlineContactField;
